import * as React from 'react';

import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { toast } from 'react-toastify';
import { Campaign } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { CAMPAIGN_STATUSES } from 'utils/models/Campaign';
import { useCurrentOrganization } from 'utils/util/hooks';

type PromotionPacketContextType = {
  campaigns?: Campaign[];
};

const PromotionPacketContext = createContext<PromotionPacketContextType | null>(
  null
);

const PromotionPacketProvider: FC<PropsWithChildren> = ({ children }) => {
  const [campaigns, setCampaigns] = React.useState<Campaign[] | undefined>(
    undefined
  );

  const currentOrganization = useCurrentOrganization();

  const handleCallback = useCallback((data: any, error: any) => {
    if (!!error || !data?.results) {
      console.error('Unexpected missing campaign results', data, error);
      toast.error('Error fetching campaigns for promotion packets');
      setCampaigns([]);
      return;
    }
    setCampaigns(data.results.filter((c: Campaign) => !!c.phases));
  }, []);

  useConfirmApi({
    url: `/campaigns`,
    method: 'GET',
    params: {
      status: CAMPAIGN_STATUSES.ACTIVE,
      organization: currentOrganization?.id,
    },
    callback: handleCallback,
  });

  const value: PromotionPacketContextType = useMemo(() => {
    return {
      campaigns,
    };
  }, [campaigns]);

  return (
    <PromotionPacketContext.Provider value={value}>
      {children}
    </PromotionPacketContext.Provider>
  );
};

const usePromotionPacket = (): PromotionPacketContextType => {
  const context = useContext(PromotionPacketContext);
  if (!context) {
    throw new Error(
      'usePromotionPacket must be used within a PromotionPacketProvider'
    );
  }
  return context;
};

export { PromotionPacketProvider, usePromotionPacket };
